<template>
  <div>
    <!-- Fundo animado Premium -->
    <PremiumBackground />

    <!-- Cabeçalho (Menu Desktop) -->
    <SiteHeader v-if="!route.meta.hideHeader" />

    <!-- Renderiza as rotas usando o sistema nativo de transição do Vue -->
    <main class="app-content-wrapper" :class="{ 'has-header-tools': auth.isAuthenticated, 'is-standalone-view': route.meta.hideHeader }">
      <router-view v-slot="{ Component, route }">
        <transition :name="transitionName" mode="out-in" :duration="500">
          <div :key="route.path" class="app-page">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </main>
    <SiteFooter v-if="!route.meta.hideFooter" />
    <SiteAppMenu v-if="!route.meta.hideAppMenu" />
    
    <!-- Efeito global de bolhas ao tocar/clicar -->
    <BubbleEffect />
    
    <!-- Sistema de Notificações Global -->
    <GlobalToasts />
    <AppDialog />

    <!-- Modal de Confirmação de Idade (+18) -->
    <AgeVerificationModal />

    <!-- Modal Bloqueante de Redefinição Obrigatória de Senha (Regra R04: v-if) -->
    <ForcePasswordChangeModal v-if="auth.isAuthenticated && auth.mustChangePassword" />

    <!-- Loading Animation (Bottom Right) -->
    <RouteLoading :isRouteLoading="isRouteLoading" />

    <!-- SVG Filter invisível para o efeito Lava Lamp (Gooey) -->
    <svg style="visibility: hidden; position: absolute;" width="0" height="0">
      <defs>
        <filter id="gooey">
          <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
          <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" result="gooey" />
          <feComposite in="SourceGraphic" in2="gooey" operator="atop"/>
        </filter>
      </defs>
    </svg>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SiteHeader from './components/SiteHeader.vue'
import SiteFooter from './components/SiteFooter.vue'
import SiteAppMenu from './components/SiteAppMenu.vue'
import PremiumBackground from './components/PremiumBackground.vue'
import BubbleEffect from './components/ui/BubbleEffect.vue'
import GlobalToasts from './components/GlobalToasts.vue'
import AppDialog from './components/AppDialog.vue'
import RouteLoading from './components/RouteLoading.vue'
import ForcePasswordChangeModal from './components/auth/ForcePasswordChangeModal.vue'
import AgeVerificationModal from './components/AgeVerificationModal.vue'
import { useThemeStore } from './stores/theme'
import { useAuthStore } from './stores/auth'

const themeStore = useThemeStore()
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const transitionName = ref('slide-left')
const isRouteLoading = ref(false)

// Intercepta as rotas para mostrar o loading
router.beforeEach((to, from, next) => {
  if (to.path !== from.path) {
    isRouteLoading.value = true
  }
  next()
})

router.afterEach((to, from) => {
  if (to.path !== from.path) {
    // Sobe para o topo somente após a nova rota carregar e a view transition iniciar
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }
  // Pequeno delay para a transição ficar visível e dar tempo da DOM atualizar
  setTimeout(() => {
    isRouteLoading.value = false
  }, 400)
})

// Define se a transição entra empurrando da esquerda ou da direita
watch(() => route.meta.index, (toIndex, fromIndex) => {
  if (toIndex !== undefined && fromIndex !== undefined) {
    transitionName.value = toIndex > fromIndex ? 'slide-left' : 'slide-right'
  }
})

onMounted(() => {
  themeStore.initTheme()
})
</script>

<style scoped>
/* Estilos globais ou específicos do App.vue, se necessários */
</style>

<style>
/* MATAR DEFINITIVAMENTE QUALQUER SCROLL HORIZONTAL (ex: .row do bootstrap vazando) */
html, body, #app {
  overflow-x: hidden !important;
  max-width: 100% !important;
  width: 100%;
}

.font-cinzel {
  font-family: 'Cinzel', serif !important;
}

.font-playfair {
  font-family: 'Playfair Display', serif !important;
}

body {
  font-family: 'Raleway', sans-serif !important;
}

:root {
  --bs-primary: #9B111E !important;
  --bs-primary-rgb: 155, 17, 30 !important;
  --cnvs-themecolor: #9B111E !important;
  --cnvs-themecolor-rgb: 155, 17, 30 !important;
}

body.dark {
  --bs-primary: #b61323 !important;
  --bs-primary-rgb: 182, 19, 35 !important;
  --cnvs-themecolor: #b61323 !important;
  --cnvs-themecolor-rgb: 182, 19, 35 !important;
}

/* Permite que o fundo premium apareça através do wrapper padrão do Canvas no modo escuro */
html body,
html body.dark,
body,
body #app,
body #wrapper,
body #content,
body .content-wrap,
body .slider-element {
  background-color: transparent !important;
}

/* Compensação suave no desktop quando a barra inferior do header (header-tools-bar) está visível */
@media (min-width: 992px) {
  .app-content-wrapper {
    transition: padding-top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* Zerado o offset extra que fazia a página subir/saltar durante as transições de rota quando logado */
  .app-content-wrapper.has-header-tools {
    padding-top: 0;
  }
}

/* Espaçamento no rodapé para compensar o Menu App Inferior no mobile */
@media (max-width: 991.98px) {
  body {
    padding-bottom: 70px !important; /* Altura do Menu App */
  }
}

/* Espaçamento suave no desktop para o rodapé fixo magro de 38px */
@media (min-width: 992px) {
  body {
    padding-bottom: 42px !important;
  }
}
</style>
